import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { KS } from "./api.jsx";
import { TIER, dealTierThreshold } from "./data.jsx";

const BROWSE_PAGE_LIMIT = 50;
const MAX_SEARCH_OFFSET = 5000;
const BROWSE_CACHE_LIMIT = 8;
const FIXED_SORT = "listing_time_desc";
const SEARCH_SORT = "search_relevance";
const SORTABLE_PUBLIC_FEEDS = new Set(["all"]);
const FEED_KEYS = ["todays_deals", "deep_discount", "steal", "deal", "negotiable", "unscored_gold", "all"];
const COUNT_KEYS = FEED_KEYS;
const DEAL_FEEDS = new Set(["todays_deals", "deep_discount", "steal", "deal", "negotiable"]);
const PRICED_STATUS = "priced";
const PRICED_REVIEW_STATUS = "priced_review_required";
const PRICED_PUBLIC_STATUSES = new Set([PRICED_STATUS, PRICED_REVIEW_STATUS]);

const itemId = (item) => item && item.id != null ? String(item.id) : "";
const finiteNumber = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const stableArray = (value) =>
  Array.isArray(value)
    ? value.map((item) => item).filter((item) => item != null && item !== "").sort()
    : [];

function normalizeBrowseFilters(filters = {}) {
  return {
    karat: stableArray(filters.karat)
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item))
      .sort((a, b) => a - b),
    type: stableArray(filters.type ?? filters.item_type).map((item) => String(item)),
  };
}

function canServerSort({ feed, searchTerm } = {}) {
  return !String(searchTerm || "").trim() && SORTABLE_PUBLIC_FEEDS.has(feed);
}

function effectiveSortForQuery({ feed, searchTerm, sort } = {}) {
  if (String(searchTerm || "").trim()) return SEARCH_SORT;
  return canServerSort({ feed, searchTerm }) ? (sort || "newest") : FIXED_SORT;
}

function makeCountsKey({ filters, includeReview } = {}) {
  return JSON.stringify({
    filters: normalizeBrowseFilters(filters),
    includeReview: includeReview !== false,
  });
}

function makeBrowseQueryKey({ feed, filters, sort, searchTerm, includeReview } = {}) {
  const q = String(searchTerm || "").trim();
  return JSON.stringify({
    feed,
    filters: normalizeBrowseFilters(filters),
    includeReview: includeReview !== false,
    searchTerm: q,
    sort: effectiveSortForQuery({ feed, searchTerm: q, sort }),
  });
}

function newItemIds(current, incoming) {
  const currentIds = new Set((current || []).map(itemId));
  return (incoming || []).map(itemId).filter((id) => id && !currentIds.has(id));
}

function mergePageItems(existing = [], incoming = [], { mode = "replace", replaceIds = [] } = {}) {
  const seen = new Set();
  if (mode === "append") {
    const byId = new Map();
    const ordered = [];
    [...existing, ...incoming].forEach((item) => {
      const key = itemId(item);
      if (!key) return;
      const next = byId.has(key) ? { ...byId.get(key), ...item } : item;
      byId.set(key, next);
      if (!seen.has(key)) {
        seen.add(key);
        ordered.push(key);
      }
    });
    return ordered.map((key) => byId.get(key)).filter(Boolean);
  }

  if (mode === "revalidate") {
    const existingById = new Map((existing || []).map((item) => [itemId(item), item]).filter(([key]) => key));
    const replaceSet = new Set((replaceIds || []).map((key) => String(key)));
    const merged = [];
    (incoming || []).forEach((item) => {
      const key = itemId(item);
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(existingById.has(key) ? { ...existingById.get(key), ...item } : item);
    });
    (existing || []).forEach((item) => {
      const key = itemId(item);
      if (!key || seen.has(key) || replaceSet.has(key)) return;
      seen.add(key);
      merged.push(item);
    });
    return merged;
  }

  return (incoming || []).filter((item) => {
    const key = itemId(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function createInitialEntry(key = "") {
  return {
    key,
    items: [],
    counts: null,
    totalCount: null,
    nextPage: null,
    loaded: false,
    loading: false,
    revalidating: false,
    loadingMore: false,
    error: null,
    stale: false,
    backgroundError: null,
    nextPageError: null,
    scrollTop: 0,
    updatedAt: 0,
    firstPageIds: [],
    lastNewIds: [],
    newItemsSeq: 0,
  };
}

function mergePageIntoEntry(entry, page, { mode = "replace", counts, totalCount, background = false } = {}) {
  const incoming = page && Array.isArray(page.items) ? page.items : [];
  const previousItems = entry.items || [];
  const incomingIds = incoming.map(itemId).filter(Boolean);
  const nextItems = mergePageItems(previousItems, incoming, {
    mode,
    replaceIds: mode === "revalidate" ? entry.firstPageIds : [],
  });
  const lastNewIds = background ? newItemIds(previousItems, incoming) : [];
  return {
    ...entry,
    items: nextItems,
    counts: counts || entry.counts,
    totalCount: totalCount ?? page?.totalCount ?? entry.totalCount,
    nextPage: mode === "revalidate" ? entry.nextPage : page?.nextPage || null,
    loaded: true,
    loading: false,
    revalidating: false,
    loadingMore: false,
    error: null,
    stale: false,
    backgroundError: null,
    nextPageError: null,
    updatedAt: Date.now(),
    firstPageIds: mode === "append" ? entry.firstPageIds : incomingIds,
    lastNewIds,
    newItemsSeq: lastNewIds.length ? entry.newItemsSeq + 1 : entry.newItemsSeq,
  };
}

function capCache(cache, activeKey, limit = BROWSE_CACHE_LIMIT) {
  if (cache.size <= limit) return cache;
  const entries = Array.from(cache.entries())
    .filter(([key]) => key !== activeKey)
    .sort((a, b) => (a[1].updatedAt || 0) - (b[1].updatedAt || 0));
  const next = new Map(cache);
  while (next.size > limit && entries.length) {
    next.delete(entries.shift()[0]);
  }
  return next;
}

function setBoundedCacheValue(cache, key, value, limit = BROWSE_CACHE_LIMIT) {
  if (!cache || !key) return cache;
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  while (cache.size > limit) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }
  return cache;
}

function updateEntryInCache(cache, key, updater) {
  const current = cache.get(key) || createInitialEntry(key);
  const nextEntry = typeof updater === "function" ? updater(current) : updater;
  if (nextEntry === current) return cache;
  const next = new Map(cache);
  next.set(key, nextEntry);
  return capCache(next, key);
}

function isSearchCapped({ searchTerm, items, totalCount, nextPage } = {}) {
  const total = Number(totalCount);
  const loaded = (items || []).length;
  return Boolean(
    String(searchTerm || "").trim() &&
    !nextPage &&
    Number.isFinite(total) &&
    total > loaded,
  );
}

function parseBrowseQueryKey(key) {
  try {
    const parsed = JSON.parse(key);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function itemListingTimeMs(item) {
  const raw = item && item.listingTimeUtc;
  const parsed = raw ? Date.parse(raw) : NaN;
  if (Number.isFinite(parsed)) return parsed;
  return null;
}

function itemRatio(item) {
  return finiteNumber(item && item.ratio);
}

function itemScore(item) {
  return finiteNumber(item && item.dealScore);
}

function itemPricingStatus(item) {
  return item?.pricingStatus == null ? null : String(item.pricingStatus);
}

function thresholdForFeed(feed) {
  const configured = dealTierThreshold(feed);
  if (Number.isFinite(configured)) return configured;
  if (feed === "todays_deals" || feed === "negotiable") return TIER.deal.max;
  return TIER[feed]?.max ?? null;
}

function itemMatchesReviewPolicy(item, includeReview) {
  return includeReview !== false || (!item?.requiresReview && !item?.weightRequiresReview);
}

function itemMatchesDealPricingPolicy(item, includeReview) {
  const status = itemPricingStatus(item);
  if (includeReview === false) {
    return status === PRICED_STATUS && item?.weightRequiresReview !== true;
  }
  return PRICED_PUBLIC_STATUSES.has(status);
}

function itemMatchesPublicFilters(item, filters = {}) {
  const normalized = normalizeBrowseFilters(filters);
  if (normalized.karat.length && !normalized.karat.includes(Number(item?.karat))) {
    return false;
  }
  if (normalized.type.length) {
    const wanted = new Set(normalized.type.map((value) => String(value).toLowerCase()));
    if (!wanted.has(String(item?.itemType || "").toLowerCase())) return false;
  }
  return true;
}

function itemMatchesSearch(item, searchTerm) {
  const q = String(searchTerm || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.title,
    item?.itemType,
    item?.seller,
    item?.primaryMetal,
  ].filter(Boolean).join(" ").toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
}

function itemMatchesFeed(item, feed, nowMs = Date.now()) {
  const ratio = itemRatio(item);
  switch (feed) {
    case "todays_deals": {
      const listedAt = itemListingTimeMs(item);
      const threshold = thresholdForFeed("todays_deals");
      return (
        itemMatchesDealPricingPolicy(item, true) &&
        ratio != null &&
        threshold != null &&
        ratio <= threshold &&
        listedAt != null &&
        nowMs - listedAt <= 24 * 60 * 60 * 1000
      );
    }
    case "deep_discount":
    case "steal":
    case "deal": {
      const threshold = thresholdForFeed(feed);
      return itemMatchesDealPricingPolicy(item, true) && ratio != null && threshold != null && ratio <= threshold;
    }
    case "negotiable": {
      const threshold = thresholdForFeed("negotiable");
      return (
        itemMatchesDealPricingPolicy(item, true) &&
        item?.bestOffer === true &&
        ratio != null &&
        threshold != null &&
        ratio <= threshold
      );
    }
    case "unscored_gold":
      return item?.isSolidGold === true && ratio == null;
    case "all":
      return true;
    default:
      return false;
  }
}

function canInsertAtHeadForSort(item, firstItem, sort, nowMs = Date.now()) {
  if (!firstItem) return sort !== SEARCH_SORT;
  switch (sort) {
    case "newest":
    case FIXED_SORT: {
      const nextTime = itemListingTimeMs(item);
      const firstTime = itemListingTimeMs(firstItem);
      if (nextTime == null) return false;
      if (firstTime == null) return true;
      return nextTime >= firstTime;
    }
    case "price_to_melt_asc": {
      const nextRatio = itemRatio(item);
      const firstRatio = itemRatio(firstItem);
      return nextRatio != null && (firstRatio == null || nextRatio <= firstRatio);
    }
    case "deal_score_desc": {
      const nextScore = itemScore(item);
      const firstScore = itemScore(firstItem);
      return nextScore != null && (firstScore == null || nextScore >= firstScore);
    }
    default:
      return false;
  }
}

function itemMatchesBrowseQuery(item, query, nowMs = Date.now()) {
  if (!query) return false;
  return (
    itemMatchesReviewPolicy(item, query.includeReview) &&
    (!DEAL_FEEDS.has(query.feed) || itemMatchesDealPricingPolicy(item, query.includeReview)) &&
    itemMatchesPublicFilters(item, query.filters) &&
    itemMatchesSearch(item, query.searchTerm) &&
    itemMatchesFeed(item, query.feed, nowMs)
  );
}

function mergeLiveItemIntoEntry(entry, item, query, { nowMs = Date.now() } = {}) {
  const id = itemId(item);
  if (!entry || !entry.loaded || !id) return entry;

  const existingIndex = (entry.items || []).findIndex((current) => itemId(current) === id);
  if (existingIndex >= 0) {
    const nextItems = [...entry.items];
    nextItems[existingIndex] = { ...nextItems[existingIndex], ...item };
    return {
      ...entry,
      items: nextItems,
      updatedAt: nowMs,
    };
  }

  if (!itemMatchesBrowseQuery(item, query, nowMs)) return entry;
  if (!canInsertAtHeadForSort(item, entry.items?.[0], query.sort, nowMs)) return entry;

  return {
    ...entry,
    items: [item, ...(entry.items || [])],
    stale: false,
    backgroundError: null,
    updatedAt: nowMs,
    firstPageIds: [id, ...(entry.firstPageIds || []).filter((current) => current !== id)],
    lastNewIds: [id],
    newItemsSeq: entry.newItemsSeq + 1,
  };
}

function mergeLiveItemIntoCache(cache, item, { activeKey, nowMs = Date.now() } = {}) {
  if (!cache || !itemId(item)) return cache;
  let next = cache;
  for (const [key, entry] of cache.entries()) {
    const query = parseBrowseQueryKey(key);
    const merged = mergeLiveItemIntoEntry(entry, item, query, { nowMs });
    if (merged !== entry) {
      if (next === cache) next = new Map(cache);
      next.set(key, merged);
    }
  }
  return next === cache ? cache : capCache(next, activeKey);
}

function useBrowseQuery({ enabled = true, feed, filters, sort, searchTerm, includeReview = true } = {}) {
  const normalizedFilters = useMemo(() => normalizeBrowseFilters(filters), [filters]);
  const trimmedSearch = String(searchTerm || "").trim();
  const queryKey = useMemo(
    () => makeBrowseQueryKey({ feed, filters: normalizedFilters, sort, searchTerm: trimmedSearch, includeReview }),
    [feed, normalizedFilters, sort, trimmedSearch, includeReview],
  );
  const countsKey = useMemo(
    () => makeCountsKey({ filters: normalizedFilters, includeReview }),
    [normalizedFilters, includeReview],
  );
  const effectiveSort = useMemo(
    () => effectiveSortForQuery({ feed, searchTerm: trimmedSearch, sort }),
    [feed, trimmedSearch, sort],
  );

  const [cache, setCache] = useState(() => new Map());
  const cacheRef = useRef(cache);
  const countsCacheRef = useRef(new Map());
  const requestTokensRef = useRef(new Map());
  const loadMoreTokensRef = useRef(new Map());
  const pendingScrollSaveRef = useRef(null);
  const scrollSaveFrameRef = useRef(null);
  const liveMergeRef = useRef({ enabled, queryKey });
  liveMergeRef.current = { enabled, queryKey };

  useEffect(() => { cacheRef.current = cache; }, [cache]);

  useEffect(() => () => {
    if (scrollSaveFrameRef.current != null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(scrollSaveFrameRef.current);
    }
    scrollSaveFrameRef.current = null;
    pendingScrollSaveRef.current = null;
  }, []);

  const setEntryForKey = useCallback((key, updater) => {
    setCache((prev) => updateEntryInCache(prev, key, updater));
  }, []);

  const fetchCounts = useCallback(async ({ force = false } = {}) => {
    if (!force && countsCacheRef.current.has(countsKey)) {
      const counts = countsCacheRef.current.get(countsKey);
      setBoundedCacheValue(countsCacheRef.current, countsKey, counts);
      return counts;
    }
    const counts = await KS.getBrowseCounts({ filters: normalizedFilters, includeReview });
    setBoundedCacheValue(countsCacheRef.current, countsKey, counts);
    return counts;
  }, [countsKey, normalizedFilters, includeReview]);

  const fetchPage = useCallback(async ({ page } = {}) => {
    if (trimmedSearch) {
      return KS.search(trimmedSearch, {
        filters: normalizedFilters,
        includeReview,
        limit: BROWSE_PAGE_LIMIT,
        page,
      });
    }
    return KS.getFeed(feed, {
      filters: normalizedFilters,
      includeReview,
      sort: effectiveSort,
      limit: BROWSE_PAGE_LIMIT,
      page,
    });
  }, [trimmedSearch, normalizedFilters, includeReview, feed, effectiveSort]);

  const loadFirstPage = useCallback(async ({ background = false, force = false } = {}) => {
    if (!enabled) return;
    const key = queryKey;
    const current = cacheRef.current.get(key);
    if (!force && current && (current.loaded || current.loading || current.revalidating)) return;
    const token = Symbol("browse-request");
    requestTokensRef.current.set(key, token);
    setEntryForKey(key, (entry) => ({
      ...entry,
      loading: !background,
      revalidating: background,
      error: background ? entry.error : null,
      backgroundError: background ? null : entry.backgroundError,
      stale: background ? entry.stale : false,
    }));
    try {
      const [counts, page] = await Promise.all([
        fetchCounts({ force }),
        fetchPage({ page: null }),
      ]);
      if (requestTokensRef.current.get(key) !== token) return;
      setEntryForKey(key, (entry) =>
        mergePageIntoEntry(entry, page, {
          mode: background ? "revalidate" : "replace",
          counts,
          totalCount: page?.totalCount ?? null,
          background,
        }),
      );
    } catch (err) {
      if (requestTokensRef.current.get(key) !== token) return;
      setEntryForKey(key, (entry) => ({
        ...entry,
        loading: false,
        revalidating: false,
        error: entry.items.length > 0 ? null : err,
        stale: entry.items.length > 0,
        backgroundError: entry.items.length > 0 ? err : entry.backgroundError,
      }));
    }
  }, [enabled, queryKey, setEntryForKey, fetchCounts, fetchPage]);

  useEffect(() => {
    if (!enabled) return;
    void loadFirstPage({ background: false, force: false });
  }, [enabled, queryKey, loadFirstPage]);

  const refresh = useCallback(({ background = false } = {}) =>
    loadFirstPage({ background, force: true }), [loadFirstPage]);

  const mergeLiveItem = useCallback((item) => {
    const { enabled: mergeEnabled, queryKey: activeKey } = liveMergeRef.current;
    if (!mergeEnabled || !itemId(item)) return;
    setCache((prev) => mergeLiveItemIntoCache(prev, item, { activeKey }));
  }, []);

  const loadMore = useCallback(async () => {
    if (!enabled) return;
    const key = queryKey;
    const current = cacheRef.current.get(key) || createInitialEntry(key);
    if (!current.nextPage || current.loading || current.revalidating || current.loadingMore) return;
    const token = Symbol("browse-load-more");
    loadMoreTokensRef.current.set(key, token);
    setEntryForKey(key, (entry) => ({ ...entry, loadingMore: true, nextPageError: null }));
    try {
      const page = await fetchPage({ page: current.nextPage });
      if (loadMoreTokensRef.current.get(key) !== token) return;
      setEntryForKey(key, (entry) => ({
        ...mergePageIntoEntry(entry, page, {
          mode: "append",
          totalCount: page?.totalCount ?? entry.totalCount,
          background: false,
        }),
        counts: entry.counts,
      }));
    } catch (err) {
      if (loadMoreTokensRef.current.get(key) !== token) return;
      setEntryForKey(key, (entry) => ({
        ...entry,
        loadingMore: false,
        nextPageError: err,
      }));
    }
  }, [enabled, queryKey, setEntryForKey, fetchPage]);

  const saveScrollTop = useCallback((scrollTop) => {
    pendingScrollSaveRef.current = {
      key: queryKey,
      scrollTop: Math.max(0, Number(scrollTop) || 0),
    };
    const flush = () => {
      scrollSaveFrameRef.current = null;
      const pending = pendingScrollSaveRef.current;
      pendingScrollSaveRef.current = null;
      if (!pending) return;
      setEntryForKey(pending.key, (entry) =>
        Math.abs((entry.scrollTop || 0) - pending.scrollTop) < 4
          ? entry
          : { ...entry, scrollTop: pending.scrollTop },
      );
    };
    if (scrollSaveFrameRef.current != null) return;
    if (typeof requestAnimationFrame === "function") {
      scrollSaveFrameRef.current = requestAnimationFrame(flush);
    } else {
      flush();
    }
  }, [queryKey, setEntryForKey]);

  const entry = cache.get(queryKey) || createInitialEntry(queryKey);
  const cachedCounts = entry.counts || countsCacheRef.current.get(countsKey) || null;
  return {
    key: queryKey,
    items: entry.items,
    counts: cachedCounts,
    totalCount: entry.totalCount,
    nextPage: entry.nextPage,
    hasMore: Boolean(entry.nextPage),
    hasItems: entry.items.length > 0,
    loading: entry.loading,
    revalidating: entry.revalidating,
    loadingMore: entry.loadingMore,
    error: entry.error,
    stale: entry.stale,
    backgroundError: entry.backgroundError,
    nextPageError: entry.nextPageError,
    scrollTop: entry.scrollTop || 0,
    lastNewIds: entry.lastNewIds,
    newItemsSeq: entry.newItemsSeq,
    searchCapped: isSearchCapped({
      searchTerm: trimmedSearch,
      items: entry.items,
      totalCount: entry.totalCount,
      nextPage: entry.nextPage,
    }),
    sortEnabled: canServerSort({ feed, searchTerm: trimmedSearch }),
    effectiveSort,
    refresh,
    loadMore,
    saveScrollTop,
    mergeLiveItem,
  };
}

export {
  BROWSE_PAGE_LIMIT,
  COUNT_KEYS,
  FEED_KEYS,
  FIXED_SORT,
  MAX_SEARCH_OFFSET,
  canServerSort,
  createInitialEntry,
  effectiveSortForQuery,
  isSearchCapped,
  itemMatchesBrowseQuery,
  makeBrowseQueryKey,
  makeCountsKey,
  mergeLiveItemIntoCache,
  mergeLiveItemIntoEntry,
  mergePageIntoEntry,
  mergePageItems,
  normalizeBrowseFilters,
  SEARCH_SORT,
  setBoundedCacheValue,
  updateEntryInCache,
  useBrowseQuery,
};
